import FusionTheme from "@directv-digital/fusion.themes.fusion";
import { Mode, Size, TextFieldDropdownStyles } from "./text-field-dropdown.types";

const { palette } = FusionTheme.fusion;

export const textFieldDropdownStyles = (
  size: Size,
  mode: Mode,
  disabled: boolean,
  error: boolean,
  shrinkLabelActive: boolean
): TextFieldDropdownStyles => ({
  FormControl: {
    ...FusionTheme.typography["Body 1"],
    "& .MuiFormControl-root": {
      height: {
        xs: "48px",
        sm: "48px",
        md: size === "small" ? "48px" : "56px"
      }
    }
  },
  InputLabel: {
    ...FusionTheme.typography["Body 1"],
    top: {
      sm: "-2px",
      md: "-2px",
      lg: "-2px"
    },
    "&.MuiInputLabel-shrink": {
      top: "2px",
      left: "2px",
      ...FusionTheme.typography["Body 3"],
      color: error ?
        mode === "dark" ? "#E3837A !important" : "#D23627 !important" :
        mode === "dark" ? "#D1D1D1 !important" : "#555555 !important",
      zIndex: 901,
      cursor: "pointer"
    }
  },
  InputLabelStyle: {
    color:
      disabled ? FusionTheme.fusion.palette["field-inactive"] :
        mode === "dark" ? FusionTheme.fusion.palette["field-inverse-label"] :
          FusionTheme.fusion.palette["field-label"]
  },
  InputContainer: {
    width: "100%",
    cursor: "pointer",
    position: "relative",
    zIndex: 900,
    margin: "0px !important",
    height: {
      xs: "48px",
      sm: "48px",
      md: size === "small" ? "48px" : "56px"
    },
    "& .DtvInputBase-root, & .MuiInputBase-root": {
      height: {
        xs: "48px",
        sm: "48px",
        md: size === "small" ? "48px" : "56px"
      },
      paddingRight: 0
    },
    "& .MuiInputLabel-root": {
      lineHeight: "20.8px !important",
      color: mode === "dark" ?
        FusionTheme.fusion.palette["field-inverse-label"] :
        FusionTheme.fusion.palette["field-label"],
      transform: shrinkLabelActive ?
          `translate(14px, ${size === 'small' ? '-10px' : '-9px'}) !important` :
        {
          xs: `translate(14px, ${size === 'small' ? '16px' : '16px'}) !important`,
          lg: `translate(14px, ${size === 'small' ? '16px' : '18px'}) !important`
        },
      fontSize: {
        xs: `${shrinkLabelActive ? "14" : "16"}px !important`,
        md: `${shrinkLabelActive ? "14" : "20"}px !important`
      }
    },
    "& .DtvInputBase-root, & .MuiOutlinedInput-root": {
      height: {
        xs: "48px",
        sm: "48px",
        md: size === "small" ? "48px" : "56px"
      },
      maxHeight: {
        xs: "48px",
        sm: "48px",
        md: size === "small" ? "48px" : "56px"
      },
      borderRadius: "4px",
      cursor: "pointer",

      "& input": {
        cursor: "pointer !important",
        padding: "0 44px 0 16px",
        height: {
          xs: "48px",
          sm: "48px",
          md: "56px"
        },
        maxHeight: {
          xs: "48px",
          sm: "48px",
          md: "56px"
        },
        textOverflow: "ellipsis",
        color: mode === "dark" ? "#FFFFFF !important" : "#102641 !important",
        fontSize: { lg: '20px' },
        "&::placeholder": {
          color: "#8D8D8D !important",
          opacity: 1
        },
        "&.Mui-disabled": {
          WebkitTextFillColor: "#8D8D8D !important"
        },
        "& .MuiInputLabel-root.Mui-focused, & .DtvInputLabel-root.Dtv-focused": {
          color: "#55555"
        }
      }
    },
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        borderColor: disabled ?
          (mode === "dark" ? "#8D8D8D !important" : "#BBBBBB !important") :
          (error ? "#E35A4D !important" : "#8D8D8D !important"),
        "& legend": {
          fontSize: "17px !important"
        },
        "& span": { paddingRight: 0 }
      },
      "&.Mui-disabled fieldset": {
        borderColor: mode === "dark" ? "#8D8D8D !important" : "#BBBBBB !important"
      },
      "&:hover fieldset": {
        borderColor: disabled ?
          (mode === "dark" ? "#D1D1D1 !important" : "#BBBBBB !important") :
          error ?
            (mode === "dark" ? "#E3837A !important" : "#AA2E23 !important") :
            (mode === "dark" ? "#BBBBBB !important" : "#102641 !important")
      },
      "&.Mui-focused fieldset": {
        borderColor: disabled ?
          (mode === "dark" ? "#8D8D8D !important" : "#BBBBBB !important") :
          error ?
            (mode === "dark" ? "#E35A4D !important" : "#E35A4D !important") :
            (mode === "dark" ? "#1FBCF8 !important" : "#3885E1 !important")
      }
    },
    "&:hover": {
      backgroundColor: mode === "dark" ?
          `${palette["menu-inverse-bg-hover"]} !important` :
          `${palette["menu-bg-hover"]} !important`
    },
    transition: "background-color 150ms ease"
  },
  MenuContainer: {
    position: "absolute",
    maxWidth: "100%",
    top: {
      xs: "52px",
      sm: "52px",
      md: size === "small" ? "52px" : "60px"
    },
    backgroundColor: "#ffffff !important",
    display: "flex",
    flexDirection: "column",
    overflowY: "auto",
    margin: 0,
    borderRadius: "4px",
    zIndex: 200,
    // custom small fusion scrollbar
    "&::-webkit-scrollbar": {
      width: "12px"
    },
    "&::-webkit-scrollbar-track": {
      background: mode === "dark" ? "#F4F4F4" : "#FFFFFF",
      borderRadius: FusionTheme.fusion.shapes["shape-small"]
    },
    "&::-webkit-scrollbar-thumb": {
      border: "4px solid transparent",
      backgroundClip: "padding-box",
      backgroundColor:
          mode === "dark" ? palette.neutral600 : palette.neutral300,
      borderRadius: "20px"
    }
  },
  MenuItem: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "0px 16px", // Any vertical padding should be 0 if isVisible = false
    height: "48px",
    cursor: "pointer",
    textOverflow: "ellipsis",
    textWrap: "nowrap",
    overflow: "hidden",
    fontSize: {
      xs: "16px",
      sm: "16px",
      md: size === "small" ? "16px" : "20px"
    },
    color: palette["menu-content"],
    "&.Mui-focusVisible": {
      backgroundColor: mode === "dark" ?
          `${palette["menu-inverse-bg-active"]} !important` :
          `${palette["menu-bg-active"]} !important`,

      "& .text-container": {
        outline: `2px solid ${mode === "dark" ?
            `${palette["accessibility-inverse-menu-focusRing"]} !important` :
            `${palette["accessibility-focusRing"]} !important`
          }`
      }

    },
    "&.Mui-selected": {
      color: `${palette["menu-bg-active"]} !important`,
      backgroundColor: mode === "dark" ?
          `${palette["menu-inverse-bg-selected"]} !important` :
          `${palette["menu-bg-selected"]} !important`,

      "&:hover": {
        color: mode === "dark" ?
            `${palette["menu-inverse-content"]} !important` :
            `${palette["menu-content"]} !important`,
        backgroundColor: mode === "dark" ?
            `${palette["menu-inverse-bg-selected-hover"]} !important` :
            `${palette["menu-bg-selected-hover"]} !important`
      },
      "&.Mui-focusVisible": {
        color: mode === "dark" ?
            `${palette["menu-inverse-content"]} !important` :
            `${palette["menu-content"]} !important`,
        backgroundColor: mode === "dark" ?
            `${palette["menu-inverse-bg-selected"]} !important` :
            `${palette["menu-bg-selected-focused"]} !important`,

        "& .text-container": {
          outline: `2px solid ${mode === "dark" ?
              `${palette["accessibility-inverse-focusRing"]} !important` :
              `${palette["accessibility-focusRing"]} !important`
            }`
        }

      }
    },
    "&:hover": {
      backgroundColor: mode === "dark" ?
          `${palette["menu-inverse-bg-hover"]} !important` :
          `${palette["menu-bg-hover"]} !important`
    },
    "&.Mui-disabled": {
      opacity: 1,
      backgroundColor: mode === "dark" ?
          `${palette["menu-inverse-bg-inactive"]} !important` :
          `${palette["menu-bg-inactive"]} !important`,
      color: mode === "dark" ?
          `${palette["menu-inverse-content-inactive"]} !important` :
          `${palette["menu-content-inactive"]} !important`
    }
  },
  FormHelperText: {
    padding: 0,
    margin: 0
  }
})
